import { AudioManager } from '@/services/AudioManager'

const ID = 'tenblock-settings'
let refreshTimer: ReturnType<typeof setInterval> | undefined

/** 시각(0~23) → 한국어 시간대 라벨. 예: 0→새벽 12시, 14→낮 2시, 20→저녁 8시 */
function hourLabel(h: number): string {
  const period = h < 6 ? '새벽' : h < 12 ? '아침' : h < 18 ? '낮' : '저녁'
  const h12 = h % 12 === 0 ? 12 : h % 12
  return `${period} ${h12}시`
}

// 설정 패널용 인라인 아이콘(currentColor)
const ICON_GEAR = '<path fill="currentColor" d="M19.14 12.94c.04-.3.06-.61.06-.94 0-.32-.02-.64-.07-.94l2.03-1.58c.18-.14.23-.41.12-.61l-1.92-3.32c-.12-.22-.37-.29-.59-.22l-2.39.96c-.5-.38-1.03-.7-1.62-.94l-.36-2.54c-.04-.24-.24-.41-.48-.41h-3.84c-.24 0-.43.17-.47.41l-.36 2.54c-.59.24-1.13.57-1.62.94l-2.39-.96c-.22-.08-.47 0-.59.22L2.74 8.87c-.12.21-.08.47.12.61l2.03 1.58c-.05.3-.09.63-.09.94s.02.64.07.94l-2.03 1.58c-.18.14-.23.41-.12.61l1.92 3.32c.12.22.37.29.59.22l2.39-.96c.5.38 1.03.7 1.62.94l.36 2.54c.05.24.24.41.48.41h3.84c.24 0 .44-.17.47-.41l.36-2.54c.59-.24 1.13-.56 1.62-.94l2.39.96c.22.08.47 0 .59-.22l1.92-3.32c.12-.22.07-.47-.12-.61l-2.01-1.58zM12 15.6c-1.98 0-3.6-1.62-3.6-3.6s1.62-3.6 3.6-3.6 3.6 1.62 3.6 3.6-1.62 3.6-3.6 3.6z"/>'
const ICON_SPEAKER = '<path fill="currentColor" d="M3 9v6h4l5 5V4L7 9H3zm13.5 3c0-1.77-1.02-3.29-2.5-4.03v8.05c1.48-.73 2.5-2.25 2.5-4.02zM14 3.23v2.06c2.89.86 5 3.54 5 6.71s-2.11 5.85-5 6.71v2.06c4.01-.91 7-4.49 7-8.77s-2.99-7.86-7-8.77z"/>'
const ICON_NOTE = '<path fill="currentColor" d="M12 3v10.55c-.59-.34-1.27-.55-2-.55-2.21 0-4 1.79-4 4s1.79 4 4 4 4-1.79 4-4V7h4V3h-6z"/>'
const ICON_CLOCK = '<path fill="currentColor" d="M11.99 2C6.47 2 2 6.48 2 12s4.47 10 9.99 10C17.52 22 22 17.52 22 12S17.52 2 11.99 2zM12 20c-4.42 0-8-3.58-8-8s3.58-8 8-8 8 3.58 8 8-3.58 8-8 8zm.5-13H11v6l5.25 3.15.75-1.23-4.5-2.67z"/>'
const ICON_SHUFFLE = '<path fill="currentColor" d="M10.59 9.17 5.41 4 4 5.41l5.17 5.17 1.42-1.41zM14.5 4l2.04 2.04L4 18.59 5.41 20 17.96 7.46 20 9.5V4h-5.5zm.33 9.41-1.41 1.41 3.13 3.13L14.5 20H20v-5.5l-2.04 2.04-3.13-3.13z"/>'

function iconSpan(path: string, color: string, size = 18): string {
  return `<span style="display:inline-flex;flex:0 0 auto;width:${size}px;height:${size}px;color:${color};"><svg viewBox="0 0 24 24" width="100%" height="100%" aria-hidden="true">${path}</svg></span>`
}

/** 설정 패널(DOM): 효과음/배경음악 볼륨·on-off + 배경음악 선택(자동/랜덤/24곡). onChange로 외부 버튼 갱신. */
export function openSettings(onChange: () => void): void {
  closeSettings()

  const backdrop = document.createElement('div')
  backdrop.id = ID
  backdrop.style.cssText =
    'position:fixed; inset:0; z-index:1200; display:flex; align-items:flex-start; justify-content:center; background:rgba(10,8,30,0.55);'

  const panel = document.createElement('div')
  panel.style.cssText =
    'margin-top:46px; width:min(300px,90vw); max-height:82vh; overflow-y:auto; background:#1a1140; border:2px solid rgba(255,255,255,0.18); border-radius:18px; box-shadow:0 12px 40px rgba(0,0,0,0.5); padding:14px; font-family:Arial,sans-serif;'

  const title = document.createElement('div')
  title.innerHTML = `${iconSpan(ICON_GEAR, '#ffe066', 22)}<span>설정</span>`
  title.style.cssText = 'display:flex; align-items:center; justify-content:center; gap:8px; font-weight:900; color:#ffe066; font-size:18px; padding:2px 0 10px;'
  panel.appendChild(title)

  // ── 갱신 콜백(클릭/토글 시 토글 라벨 + 선택 목록 동기화) ──
  const renderers: Array<() => void> = []
  const refresh = () => { renderers.forEach((r) => r()); onChange() }

  const sectionLabel = (iconPath: string, text: string): HTMLDivElement => {
    const d = document.createElement('div')
    d.innerHTML = `${iconSpan(iconPath, '#ffe066', 18)}<span>${text}</span>`
    d.style.cssText = 'display:flex; align-items:center; gap:8px; font-weight:900; color:#ffffff; font-size:15px; margin:12px 2px 6px;'
    return d
  }

  const makeRow = (getOn: () => boolean, setOn: (v: boolean) => void, getVol: () => number, setVol: (v: number) => void): HTMLDivElement => {
    const row = document.createElement('div')
    row.style.cssText = 'display:flex; align-items:center; gap:10px; padding:2px 2px 6px;'

    const tg = document.createElement('button')
    tg.type = 'button'
    tg.style.cssText = 'padding:7px 12px; border:none; border-radius:10px; cursor:pointer; font-weight:800; font-size:13px; min-width:58px;'
    const renderTg = () => {
      const on = getOn()
      tg.textContent = on ? '켜짐' : '꺼짐'
      tg.style.background = on ? '#2e7d5b' : '#5a2e3e'
      tg.style.color = on ? '#ffffff' : '#ffb3b3'
    }
    tg.addEventListener('click', (e) => { e.stopPropagation(); AudioManager.play('ui_click', 0.3); setOn(!getOn()); refresh() })
    renderers.push(renderTg)
    renderTg()

    const sl = document.createElement('input')
    sl.type = 'range'; sl.min = '0'; sl.max = '100'; sl.value = String(Math.round(getVol() * 100))
    sl.style.cssText = 'flex:1; accent-color:#ffe066; height:22px;'
    sl.addEventListener('input', (e) => { e.stopPropagation(); setVol(parseInt(sl.value, 10) / 100) })

    row.append(tg, sl)
    return row
  }

  // 효과음
  panel.appendChild(sectionLabel(ICON_SPEAKER, '효과음'))
  panel.appendChild(makeRow(
    () => AudioManager.isSfxOn(), (v) => AudioManager.setSfx(v),
    () => AudioManager.getSfxVolume(), (v) => AudioManager.setSfxVolume(v),
  ))

  // 배경음악
  panel.appendChild(sectionLabel(ICON_NOTE, '배경음악'))
  panel.appendChild(makeRow(
    () => AudioManager.isBgmOn(), (v) => AudioManager.setBgm(v),
    () => AudioManager.getBgmVolume(), (v) => AudioManager.setBgmVolume(v),
  ))

  // 배경음악 선택
  const sub = document.createElement('div')
  sub.textContent = '— 음악 선택 —'
  sub.style.cssText = 'color:#9b86d6; font-size:12px; text-align:center; margin:8px 0 4px;'
  panel.appendChild(sub)

  const list = document.createElement('div')
  panel.appendChild(list)

  const item = (label: string, active: boolean, onClick: () => void, iconPath?: string, playing = false): HTMLButtonElement => {
    const b = document.createElement('button')
    b.type = 'button'
    const dot = active ? '<span style="color:#7ef0c2;font-weight:900;">●</span>' : ''
    const icon = iconPath ? iconSpan(iconPath, active ? '#ffffff' : '#cdbfff', 17) : ''
    const badge = playing ? '<span style="margin-left:auto;color:#7ef0c2;font-size:11px;font-weight:900;white-space:nowrap;">▶ 재생 중</span>' : ''
    b.innerHTML = `<span style="display:flex;align-items:center;gap:7px;">${dot}${icon}<span>${label}</span>${badge}</span>`
    b.style.cssText =
      `display:block; width:100%; text-align:left; padding:9px 14px; margin:3px 0; border:none; border-radius:11px; cursor:pointer; font-size:14px; font-weight:800; font-family:Arial,sans-serif; background:${active ? '#2e7d5b' : '#140d2e'}; color:${active ? '#ffffff' : '#cdbfff'};`
    b.addEventListener('click', (e) => { e.stopPropagation(); AudioManager.play('ui_click', 0.3); onClick(); refresh() })
    return b
  }

  const rebuildList = () => {
    list.innerHTML = ''
    const on = AudioManager.isBgmOn()
    const mode = AudioManager.getBgmMode()
    const mh = AudioManager.getManualHour()
    const cur = AudioManager.getCurrentHour() // 현재 실제 재생 중인 시각
    list.appendChild(item('자동 (시간대별)', on && mode === 'auto', () => AudioManager.setBgmMode('auto'), ICON_CLOCK))
    list.appendChild(item('랜덤 재생', on && mode === 'random', () => AudioManager.setBgmMode('random'), ICON_SHUFFLE))
    for (let h = 0; h < 24; h++) {
      list.appendChild(item(hourLabel(h), on && mode === 'manual' && mh === h, () => AudioManager.setBgmMode('manual', h), undefined, on && cur === h))
    }
  }
  renderers.push(rebuildList)
  rebuildList()
  // 현재 재생 곡 표시를 최신으로(랜덤 진행/선택 로드 후 갱신)
  refreshTimer = setInterval(rebuildList, 1500)

  const closeBtn = document.createElement('button')
  closeBtn.type = 'button'
  closeBtn.textContent = '닫기'
  closeBtn.style.cssText = 'display:block; width:100%; margin-top:12px; padding:11px; border:none; border-radius:12px; cursor:pointer; font-weight:900; font-size:15px; background:#a98bff; color:#ffffff;'
  closeBtn.addEventListener('click', (e) => { e.stopPropagation(); AudioManager.play('ui_click', 0.3); closeSettings() })
  panel.appendChild(closeBtn)

  // 클릭스루 방지: 포인터/터치 이벤트가 캔버스(Phaser)로 전파돼 뒤 메뉴가 눌리는 현상 차단
  const stopPointer = (e: Event) => e.stopPropagation()
  ;['pointerdown', 'pointerup', 'pointermove', 'mousedown', 'mouseup', 'touchstart', 'touchend', 'touchmove', 'wheel'].forEach((ev) => backdrop.addEventListener(ev, stopPointer))
  backdrop.addEventListener('click', () => closeSettings())
  panel.addEventListener('click', (e) => e.stopPropagation())
  backdrop.appendChild(panel)
  document.body.appendChild(backdrop)
}

export function closeSettings(): void {
  if (refreshTimer) { clearInterval(refreshTimer); refreshTimer = undefined }
  document.getElementById(ID)?.remove()
}
